// src/components/kit/link-in-bio/LinkInBioBuilder.tsx

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  useLinkInBioPage,
  useCreateLinkInBioPage,
  useUpdateLinkInBioPage,
  useGetUploadUrl,
} from '@/hooks/useLinkInBio';
import type {
  LinkInBioFormState,
  LinkItem,
  StylePreset,
  PalettePreset,
  BackgroundPreset,
} from '@/types/linkInBio';
import { STYLES, PALETTES } from './themes';
import LinkInBioRenderer from './LinkInBioRenderer';
import Spinner from '@/components/common/Spinner';

const DEFAULT_FORM_STATE: LinkInBioFormState = {
  slug: '',
  display_name: '',
  tagline: '',
  avatar_key: null,
  avatar_url: null,
  avatar_position: 'center',
  style: 'modern',
  palette: 'dark-gold',
  background: 'solid',
  smart_music_link: null,
  links: [],
  email_capture: {
    enabled: false,
    prompt_text: 'Join my mailing list',
    button_label: 'Subscribe',
  },
  campaign_banner_enabled: false,
  campaign_banner_text: '',
  published: false,
};

function generateLinkId(): string {
  return `link_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// --- Music link platform configuration ---

type MusicPlatformKey =
  | 'spotify'
  | 'apple_music'
  | 'soundcloud'
  | 'youtube_music'
  | 'tidal'
  | 'deezer'
  | 'amazon_music';

interface MusicPlatformConfig {
  key: MusicPlatformKey;
  label: string;
  domain: string;
  placeholder: string;
}

const MUSIC_PLATFORMS: MusicPlatformConfig[] = [
  { key: 'spotify', label: 'Spotify', domain: 'spotify.com', placeholder: 'https://open.spotify.com/track/...' },
  { key: 'apple_music', label: 'Apple Music', domain: 'apple.com', placeholder: 'https://music.apple.com/...' },
  { key: 'soundcloud', label: 'SoundCloud', domain: 'soundcloud.com', placeholder: 'https://soundcloud.com/...' },
  { key: 'youtube_music', label: 'YouTube Music', domain: 'youtube.com', placeholder: 'https://music.youtube.com/...' },
  { key: 'tidal', label: 'Tidal', domain: 'tidal.com', placeholder: 'https://tidal.com/...' },
  { key: 'deezer', label: 'Deezer', domain: 'deezer.com', placeholder: 'https://www.deezer.com/...' },
  { key: 'amazon_music', label: 'Amazon Music', domain: 'amazon.com', placeholder: 'https://music.amazon.com/...' },
];

const MUSIC_PLATFORM_MAP = Object.fromEntries(
  MUSIC_PLATFORMS.map((p) => [p.key, p]),
) as Record<MusicPlatformKey, MusicPlatformConfig>;

function validateMusicUrl(url: string, domain: string): string | null {
  if (!url) return null; // Empty is fine — platform is optional
  if (!url.startsWith('https://') && !url.startsWith('http://')) {
    return 'URL must start with https:// or http://';
  }
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    if (!hostname.endsWith(domain)) {
      return `URL must be a ${domain} link`;
    }
  } catch {
    return 'Invalid URL';
  }
  return null;
}

// --- Avatar Crop Modal ---

interface AvatarCropModalProps {
  imageUrl: string;
  onConfirm: (position: string) => void;
  onCancel: () => void;
}

function AvatarCropModal({ imageUrl, onConfirm, onCancel }: AvatarCropModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const [offset, setOffset] = useState(50); // percentage along the longer axis
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{ startOffset: number; startPos: number } | null>(null);

  const isWide = naturalWidth > naturalHeight;
  const isSquare = naturalWidth === naturalHeight;

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setNaturalWidth(img.naturalWidth);
      setNaturalHeight(img.naturalHeight);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const getPosition = (pct: number): string => {
    if (isSquare) return 'center';
    if (isWide) return `${pct}% 50%`;
    return `50% ${pct}%`;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setDragging(true);
    const pos = isWide ? e.clientX : e.clientY;
    dragStartRef.current = { startOffset: offset, startPos: pos };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging || !dragStartRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const size = isWide ? rect.width : rect.height;
    const currentPos = isWide ? e.clientX : e.clientY;
    const delta = currentPos - dragStartRef.current.startPos;
    // Invert: dragging right/down should decrease the percentage (move image left/up)
    const pctDelta = -(delta / size) * 100;
    const newOffset = Math.min(100, Math.max(0, dragStartRef.current.startOffset + pctDelta));
    setOffset(newOffset);
  };

  const handlePointerUp = () => {
    setDragging(false);
    dragStartRef.current = null;
  };

  const handleConfirm = () => {
    onConfirm(getPosition(offset));
  };

  if (!naturalWidth || !naturalHeight) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="rounded-xl bg-neutral-200 p-6">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-xl border border-neutral-300 bg-neutral-200 p-6 shadow-xl">
        <h3 className="mb-1 text-base font-semibold text-neutral-800">Position Your Photo</h3>
        <p className="mb-4 text-xs text-neutral-500">
          {isSquare
            ? 'Your image is square — no adjustment needed.'
            : 'Drag the image to choose which part is shown in the circle.'}
        </p>

        {/* Preview circle */}
        <div className="mb-4 flex justify-center">
          <div
            ref={containerRef}
            className="relative h-40 w-40 overflow-hidden rounded-full border-2 border-neutral-400"
            style={{ cursor: isSquare ? 'default' : 'grab', touchAction: 'none' }}
            onPointerDown={isSquare ? undefined : handlePointerDown}
            onPointerMove={isSquare ? undefined : handlePointerMove}
            onPointerUp={isSquare ? undefined : handlePointerUp}
          >
            <img
              src={imageUrl}
              alt="Crop preview"
              className="h-full w-full object-cover select-none"
              style={{ objectPosition: getPosition(offset) }}
              draggable={false}
            />
          </div>
        </div>

        {/* Slider fallback for fine control */}
        {!isSquare && (
          <div className="mb-4">
            <input
              type="range"
              min={0}
              max={100}
              value={offset}
              onChange={(e) => setOffset(Number(e.target.value))}
              className="w-full accent-primary-600"
            />
            <div className="flex justify-between text-xs text-neutral-500">
              <span>{isWide ? 'Left' : 'Top'}</span>
              <span>{isWide ? 'Right' : 'Bottom'}</span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleConfirm}
            className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-primary-700"
          >
            Confirm
          </button>
          <button
            onClick={onCancel}
            className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Builder Component ---

export default function LinkInBioBuilder() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');

  const { data: pageData, isLoading } = useLinkInBioPage();
  const createMutation = useCreateLinkInBioPage();
  const updateMutation = useUpdateLinkInBioPage();
  const uploadMutation = useGetUploadUrl();

  const isExisting = !!pageData?.page;

  const [form, setForm] = useState<LinkInBioFormState>(DEFAULT_FORM_STATE);
  const [musicLabel, setMusicLabel] = useState('Listen Now');
  const [musicPlatformUrls, setMusicPlatformUrls] = useState<Record<string, string>>({});
  const [musicPlatformErrors, setMusicPlatformErrors] = useState<Record<string, string>>({});
  const [activeMusicPlatforms, setActiveMusicPlatforms] = useState<MusicPlatformKey[]>([]);
  const [showMusicPlatformPicker, setShowMusicPlatformPicker] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Track whether the user has made changes since the last save/load
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Avatar crop modal state
  const [cropModalUrl, setCropModalUrl] = useState<string | null>(null);
  const [pendingAvatarKey, setPendingAvatarKey] = useState<string | null>(null);
  const [pendingAvatarPublicUrl, setPendingAvatarPublicUrl] = useState<string | null>(null);

  // Initialize form from existing page data
  useEffect(() => {
    if (pageData?.page) {
      const p = pageData.page;
      setForm({
        slug: p.slug,
        display_name: p.display_name,
        tagline: p.tagline,
        avatar_key: null,
        avatar_url: p.avatar_url,
        avatar_position: p.avatar_position || 'center',
        style: p.style,
        palette: p.palette,
        background: p.background,
        smart_music_link: p.smart_music_link,
        links: p.links,
        email_capture: p.email_capture,
        campaign_banner_enabled: p.campaign_banner_enabled,
        campaign_banner_text: p.campaign_banner_text || '',
        published: p.published,
      });
      if (p.smart_music_link?.label) {
        setMusicLabel(p.smart_music_link.label);
      }
      // Initialize platform URLs and active list from existing smart_music_link
      if (p.smart_music_link?.platforms) {
        setMusicPlatformUrls(p.smart_music_link.platforms);
        const keys = Object.keys(p.smart_music_link.platforms).filter(
          (k) => k in MUSIC_PLATFORM_MAP,
        ) as MusicPlatformKey[];
        setActiveMusicPlatforms(keys);
      }
      setHasUnsavedChanges(false);
    }
  }, [pageData]);

  // Warn user before closing/refreshing the tab with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const updateField = useCallback(<K extends keyof LinkInBioFormState>(
    key: K,
    value: LinkInBioFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  }, []);

  // Handle avatar upload
  const handleAvatarUpload = async (file: File) => {
    try {
      const result = await uploadMutation.mutateAsync({
        filename: file.name,
        contentType: file.type,
      });

      // Upload to S3
      await fetch(result.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      // Check if image is square
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth === img.naturalHeight) {
          // Square image — apply directly
          updateField('avatar_key', result.object_key);
          updateField('avatar_url', result.public_url);
          updateField('avatar_position', 'center');
        } else {
          // Non-square — show crop modal
          setPendingAvatarKey(result.object_key);
          setPendingAvatarPublicUrl(result.public_url);
          setCropModalUrl(result.public_url);
        }
      };
      img.onerror = () => {
        // Fallback: apply directly if we can't load the image
        updateField('avatar_key', result.object_key);
        updateField('avatar_url', result.public_url);
        updateField('avatar_position', 'center');
      };
      img.src = result.public_url;
    } catch (err) {
      console.error('Avatar upload failed:', err);
    }
  };

  const handleCropConfirm = (position: string) => {
    if (pendingAvatarKey && pendingAvatarPublicUrl) {
      updateField('avatar_key', pendingAvatarKey);
      updateField('avatar_url', pendingAvatarPublicUrl);
      updateField('avatar_position', position);
    }
    setCropModalUrl(null);
    setPendingAvatarKey(null);
    setPendingAvatarPublicUrl(null);
  };

  const handleCropCancel = () => {
    // Still apply the image, just use center
    if (pendingAvatarKey && pendingAvatarPublicUrl) {
      updateField('avatar_key', pendingAvatarKey);
      updateField('avatar_url', pendingAvatarPublicUrl);
      updateField('avatar_position', 'center');
    }
    setCropModalUrl(null);
    setPendingAvatarKey(null);
    setPendingAvatarPublicUrl(null);
  };

  // Music platform handlers
  const handleAddMusicPlatform = (key: MusicPlatformKey) => {
    if (!activeMusicPlatforms.includes(key)) {
      setActiveMusicPlatforms((prev) => [...prev, key]);
      setHasUnsavedChanges(true);
    }
    setShowMusicPlatformPicker(false);
  };

  const handleRemoveMusicPlatform = (key: MusicPlatformKey) => {
    setActiveMusicPlatforms((prev) => prev.filter((k) => k !== key));
    setMusicPlatformUrls((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setMusicPlatformErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const handleMusicPlatformUrlChange = (platformKey: string, url: string) => {
    setMusicPlatformUrls((prev) => ({ ...prev, [platformKey]: url }));
    // Clear error for this platform when user types
    setMusicPlatformErrors((prev) => {
      const next = { ...prev };
      delete next[platformKey];
      return next;
    });
    setHasUnsavedChanges(true);
  };

  // Platforms available to add (not yet active)
  const availableMusicPlatforms = MUSIC_PLATFORMS.filter(
    (p) => !activeMusicPlatforms.includes(p.key),
  );

  // Validate all music platform URLs and build smart_music_link
  const buildSmartMusicLink = (): { valid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    for (const key of activeMusicPlatforms) {
      const platform = MUSIC_PLATFORM_MAP[key];
      const url = (musicPlatformUrls[key] || '').trim();
      if (url) {
        const error = validateMusicUrl(url, platform.domain);
        if (error) {
          errors[key] = error;
        }
      }
    }
    return { valid: Object.keys(errors).length === 0, errors };
  };

  // Add a new link
  const addLink = () => {
    const newLink: LinkItem = {
      link_id: generateLinkId(),
      label: '',
      url: '',
      icon: 'other',
      position: form.links.length,
    };
    updateField('links', [...form.links, newLink]);
  };

  // Update a link
  const updateLink = (index: number, updates: Partial<LinkItem>) => {
    const newLinks = [...form.links];
    newLinks[index] = { ...newLinks[index], ...updates };
    updateField('links', newLinks);
  };

  // Remove a link
  const removeLink = (index: number) => {
    const newLinks = form.links.filter((_, i) => i !== index);
    updateField('links', newLinks.map((l, i) => ({ ...l, position: i })));
  };

  // Move link up/down
  const moveLink = (index: number, direction: 'up' | 'down') => {
    const newLinks = [...form.links];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newLinks.length) return;
    [newLinks[index], newLinks[targetIndex]] = [newLinks[targetIndex], newLinks[index]];
    updateField('links', newLinks.map((l, i) => ({ ...l, position: i })));
  };

  // Navigate away with unsaved-changes confirmation
  const navigateWithGuard = (path: string) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }
    navigate(path);
  };

  // Save / Publish — performs a full create or update
  const handleSave = async (publish: boolean) => {
    setSaveStatus('saving');
    setSaveError(null);

    // Validate music platform URLs
    const { valid, errors } = buildSmartMusicLink();
    if (!valid) {
      setMusicPlatformErrors(errors);
      setSaveStatus('error');
      setSaveError('Please fix the music link errors.');
      return;
    }

    try {
      // Build smart_music_link from manual entries
      const platforms: Record<string, string> = {};
      for (const key of activeMusicPlatforms) {
        const url = (musicPlatformUrls[key] || '').trim();
        if (url) {
          platforms[key] = url;
        }
      }

      const smartMusicLink = Object.keys(platforms).length > 0
        ? { label: musicLabel, platforms }
        : null;

      const payload = { ...form, published: publish, smart_music_link: smartMusicLink };

      if (isExisting) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus('idle'), 2000);

      if (!isExisting) {
        navigate('/app/kit/link-in-bio');
      }
    } catch (err: any) {
      setSaveStatus('error');
      if (err?.code === 'SLUG_TAKEN') {
        setSaveError('This URL slug is already in use. Please choose a different one.');
      } else {
        setSaveError(err?.message || 'Something went wrong. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const formContent = (
    <div className="space-y-6 p-6">
      {/* Back button */}
      <button
        onClick={() => navigateWithGuard('/app/kit/link-in-bio')}
        className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <h2 className="text-lg font-bold text-neutral-800">
        {isExisting ? 'Edit Page' : 'Create Page'}
      </h2>

      {/* Profile Section */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Profile</h3>

        {/* Avatar */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Avatar</label>
          <div className="flex items-center gap-4">
            {form.avatar_url ? (
              <img
                src={form.avatar_url}
                alt="Avatar"
                className="h-16 w-16 rounded-full object-cover"
                style={{ objectPosition: form.avatar_position || 'center' }}
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-300">
                <svg className="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
            <div className="flex gap-2">
              <label className="cursor-pointer rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300">
                Upload
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAvatarUpload(file);
                  }}
                />
              </label>
              {form.avatar_url && (
                <button
                  onClick={() => {
                    // Re-open crop modal for existing avatar
                    setCropModalUrl(form.avatar_url);
                    setPendingAvatarKey(form.avatar_key);
                    setPendingAvatarPublicUrl(form.avatar_url);
                  }}
                  className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300"
                >
                  Reposition
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Slug */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">URL Slug</label>
          <div className="flex items-center gap-1">
            <span className="flex-shrink-0 text-xs text-neutral-500">bandjerry.link/</span>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => {
                updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                // Clear slug-related save error when user edits the slug
                if (saveError) setSaveError(null);
              }}
              placeholder="your-name"
              className="min-w-0 flex-1 rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
            />
          </div>
        </div>

        {/* Display Name */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Display Name</label>
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => updateField('display_name', e.target.value)}
            placeholder="Your artist name"
            maxLength={100}
            className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
          />
        </div>

        {/* Tagline */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Tagline</label>
          <input
            type="text"
            value={form.tagline}
            onChange={(e) => updateField('tagline', e.target.value)}
            placeholder="Short bio or description"
            maxLength={200}
            className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
          />
        </div>
      </section>

      {/* Music Links */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Music Links</h3>
          <div className="relative">
            <button
              onClick={() => setShowMusicPlatformPicker(!showMusicPlatformPicker)}
              disabled={availableMusicPlatforms.length === 0}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              + Add Music Link
            </button>

            {/* Platform picker dropdown */}
            {showMusicPlatformPicker && availableMusicPlatforms.length > 0 && (
              <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-neutral-300 bg-neutral-100 py-1 shadow-lg">
                {availableMusicPlatforms.map((platform) => (
                  <button
                    key={platform.key}
                    onClick={() => handleAddMusicPlatform(platform.key)}
                    className="flex w-full items-center px-3 py-2 text-left text-sm text-neutral-700 transition-colors hover:bg-neutral-200"
                  >
                    {platform.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {activeMusicPlatforms.length > 0 && (
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700">Button Label</label>
            <input
              type="text"
              value={musicLabel}
              onChange={(e) => {
                setMusicLabel(e.target.value);
                setHasUnsavedChanges(true);
              }}
              placeholder="Listen Now"
              maxLength={50}
              className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
            />
          </div>
        )}

        {activeMusicPlatforms.map((key) => {
          const platform = MUSIC_PLATFORM_MAP[key];
          return (
            <div key={key} className="space-y-2 rounded-lg border border-neutral-300 bg-neutral-100 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-neutral-600">{platform.label}</span>
                <button
                  onClick={() => handleRemoveMusicPlatform(key)}
                  className="rounded p-1 text-red-400 hover:bg-red-900/20"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <input
                type="url"
                value={musicPlatformUrls[key] || ''}
                onChange={(e) => handleMusicPlatformUrlChange(key, e.target.value)}
                placeholder={platform.placeholder}
                className={`w-full rounded-lg border bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:outline-none ${
                  musicPlatformErrors[key]
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-neutral-300 focus:border-primary-600'
                }`}
              />
              {musicPlatformErrors[key] && (
                <p className="text-xs text-red-500">{musicPlatformErrors[key]}</p>
              )}
            </div>
          );
        })}

        {activeMusicPlatforms.length === 0 && (
          <p className="text-center text-sm text-neutral-500">
            No music links yet. Add a streaming platform above.
          </p>
        )}
      </section>

      {/* Campaign Banner */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Campaign Banner</h3>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.campaign_banner_enabled}
            onChange={(e) => updateField('campaign_banner_enabled', e.target.checked)}
            className="h-4 w-4 rounded border-neutral-400 accent-primary-600"
          />
          <span className="text-sm text-neutral-700">Show campaign banner</span>
        </label>
        {form.campaign_banner_enabled && (
          <input
            type="text"
            value={form.campaign_banner_text}
            onChange={(e) => updateField('campaign_banner_text', e.target.value)}
            placeholder="Currently promoting: New Single"
            maxLength={200}
            className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
          />
        )}
      </section>

      {/* Links */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">General Links</h3>
          <button
            onClick={addLink}
            disabled={form.links.length >= 20}
            className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            + Add Link
          </button>
        </div>

        {form.links.map((link, index) => (
          <div key={link.link_id} className="space-y-2 rounded-lg border border-neutral-300 bg-neutral-100 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-neutral-500">Link {index + 1}</span>
              <div className="flex gap-1">
                <button onClick={() => moveLink(index, 'up')} disabled={index === 0} className="rounded p-1 text-neutral-500 hover:bg-neutral-300 disabled:opacity-30">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                </button>
                <button onClick={() => moveLink(index, 'down')} disabled={index === form.links.length - 1} className="rounded p-1 text-neutral-500 hover:bg-neutral-300 disabled:opacity-30">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                <button onClick={() => removeLink(index)} className="rounded p-1 text-red-400 hover:bg-red-900/20">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <input
              type="text"
              value={link.label}
              onChange={(e) => updateLink(index, { label: e.target.value })}
              placeholder="Link label"
              maxLength={100}
              className="w-full rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
            />
            <input
              type="url"
              value={link.url}
              onChange={(e) => updateLink(index, { url: e.target.value })}
              placeholder="https://..."
              className="w-full rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
            />
          </div>
        ))}

        {form.links.length === 0 && (
          <p className="text-center text-sm text-neutral-500">No links yet. Add your first link above.</p>
        )}
      </section>

      {/* Email Capture */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Email Capture</h3>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.email_capture.enabled}
            onChange={(e) =>
              updateField('email_capture', { ...form.email_capture, enabled: e.target.checked })
            }
            className="h-4 w-4 rounded border-neutral-400 accent-primary-600"
          />
          <span className="text-sm text-neutral-700">Enable email signup form</span>
        </label>
        {form.email_capture.enabled && (
          <>
            <input
              type="text"
              value={form.email_capture.prompt_text}
              onChange={(e) =>
                updateField('email_capture', { ...form.email_capture, prompt_text: e.target.value })
              }
              placeholder="Join my mailing list"
              maxLength={100}
              className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
            />
            <input
              type="text"
              value={form.email_capture.button_label}
              onChange={(e) =>
                updateField('email_capture', { ...form.email_capture, button_label: e.target.value })
              }
              placeholder="Subscribe"
              maxLength={30}
              className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none"
            />
          </>
        )}
      </section>

      {/* Theme Section */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Theme</h3>

        {/* Style */}
        <div>
          <label className="mb-2 block text-xs font-medium text-neutral-500">Style</label>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(STYLES) as StylePreset[]).map((key) => (
              <button
                key={key}
                onClick={() => updateField('style', key)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  form.style === key
                    ? 'border-primary-600 bg-primary-100/20'
                    : 'border-neutral-300 hover:border-neutral-400'
                }`}
              >
                <p className="text-sm font-medium text-neutral-700">{STYLES[key].name}</p>
                <p className="text-xs text-neutral-500">{STYLES[key].description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Palette */}
        <div>
          <label className="mb-2 block text-xs font-medium text-neutral-500">Color Palette</label>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(PALETTES) as PalettePreset[]).map((key) => (
              <button
                key={key}
                onClick={() => updateField('palette', key)}
                className={`flex items-center gap-2 rounded-lg border p-3 transition-colors ${
                  form.palette === key
                    ? 'border-primary-600 bg-primary-100/20'
                    : 'border-neutral-300 hover:border-neutral-400'
                }`}
              >
                <div className={`h-5 w-5 rounded-full ${PALETTES[key].bg} border border-neutral-400`} />
                <span className="text-sm font-medium text-neutral-700">{PALETTES[key].name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Background */}
        <div>
          <label className="mb-2 block text-xs font-medium text-neutral-500">Background</label>
          <div className="flex gap-2">
            {(['solid', 'gradient'] as BackgroundPreset[]).map((bg) => (
              <button
                key={bg}
                onClick={() => updateField('background', bg)}
                className={`flex-1 rounded-lg border p-3 text-center text-sm font-medium capitalize transition-colors ${
                  form.background === bg
                    ? 'border-primary-600 bg-primary-100/20 text-primary-600'
                    : 'border-neutral-300 text-neutral-700 hover:border-neutral-400'
                }`}
              >
                {bg}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Save error message */}
      {saveError && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-700">{saveError}</p>
        </div>
      )}

      {/* Action button */}
      <div className="pb-6">
        <button
          onClick={() => handleSave(true)}
          disabled={isSaving}
          className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-black transition-colors hover:bg-primary-700 disabled:opacity-50"
        >
          {saveStatus === 'saving' ? 'Publishing...' : saveStatus === 'saved' ? '✓ Published!' : 'Publish'}
        </button>
      </div>
    </div>
  );

  const previewContent = (
    <div className="flex h-full flex-col">
      {/* Device toggle */}
      {!isMobile && (
        <div className="flex items-center justify-center gap-2 border-b border-neutral-300 bg-neutral-200 p-2">
          <button
            onClick={() => setPreviewDevice('mobile')}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${
              previewDevice === 'mobile' ? 'bg-neutral-300 text-neutral-800' : 'text-neutral-500'
            }`}
          >
            📱 Mobile
          </button>
          <button
            onClick={() => setPreviewDevice('desktop')}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${
              previewDevice === 'desktop' ? 'bg-neutral-300 text-neutral-800' : 'text-neutral-500'
            }`}
          >
            🖥️ Desktop
          </button>
        </div>
      )}

      {/* Preview */}
      <div className="flex flex-1 items-start justify-center overflow-y-auto bg-neutral-100 p-4">
        <div
          className={`overflow-hidden rounded-2xl border-4 border-neutral-400 shadow-xl ${
            previewDevice === 'mobile' ? 'w-[390px]' : 'w-full max-w-2xl'
          }`}
          style={previewDevice === 'mobile' ? { height: '700px' } : { minHeight: '600px' }}
        >
          <div className="h-full overflow-y-auto">
            <LinkInBioRenderer data={form} isPreview />
          </div>
        </div>
      </div>
    </div>
  );

  // Mobile: tabbed layout
  if (isMobile) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex border-b border-neutral-300 bg-neutral-200">
          <button
            onClick={() => setActiveTab('edit')}
            className={`flex-1 py-3 text-center text-sm font-medium ${
              activeTab === 'edit' ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-500'
            }`}
          >
            Edit
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex-1 py-3 text-center text-sm font-medium ${
              activeTab === 'preview' ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-500'
            }`}
          >
            Preview
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'edit' ? formContent : previewContent}
        </div>

        {/* Avatar crop modal */}
        {cropModalUrl && (
          <AvatarCropModal
            imageUrl={cropModalUrl}
            onConfirm={handleCropConfirm}
            onCancel={handleCropCancel}
          />
        )}
      </div>
    );
  }

  // Desktop: split pane
  return (
    <div className="flex h-full">
      <div className="w-1/2 overflow-y-auto border-r border-neutral-300">
        {formContent}
      </div>
      <div className="w-1/2">
        {previewContent}
      </div>

      {/* Avatar crop modal */}
      {cropModalUrl && (
        <AvatarCropModal
          imageUrl={cropModalUrl}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}
